import * as React from "react"
import { Head, router } from "@inertiajs/react"
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Ban,
  CheckCircle2,
  Clock,
  Loader2,
  Upload,
  XCircle,
  AlertCircle,
  Trash2,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { MonthlyUploadAlert } from "@/components/MonthlyUploadAlert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  DataCard,
  DataCardField,
  DataCardGrid,
  DataCardHeader,
  DataCardStatus,
  DataCardTitle,
} from "@/components/ui/data-card"
import { Input } from "@/components/ui/input"
import { MobileFilterSheet } from "@/components/ui/mobile-filter-sheet"
import { MobileFilterSortBar } from "@/components/ui/mobile-filter-sort-bar"
import { MobileSortSheet, type SortOption } from "@/components/ui/mobile-sort-sheet"
import { useMobileFilterSort } from "@/hooks/use-mobile-filter-sort"
import { Select } from "@/components/ui/select"
import { consumer } from "@/lib/actioncable"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog"

const SORT_OPTIONS: SortOption[] = [
  { sort: "created_at", direction: "desc", label: "Tanggal terbaru" },
  { sort: "created_at", direction: "asc", label: "Tanggal terlama" },
  { sort: "period", direction: "desc", label: "Periode terbaru" },
  { sort: "period", direction: "asc", label: "Periode terlama" },
  { sort: "active_row_count", direction: "desc", label: "Baris active terbanyak" },
  { sort: "active_row_count", direction: "asc", label: "Baris active terendah" },
  { sort: "inactive_row_count", direction: "desc", label: "Baris inactive terbanyak" },
  { sort: "inactive_row_count", direction: "asc", label: "Baris inactive terendah" },
  { sort: "total_row_count", direction: "desc", label: "Total baris terbanyak" },
  { sort: "total_row_count", direction: "asc", label: "Total baris terendah" },
  { sort: "status", direction: "asc", label: "Status A–Z" },
  { sort: "status", direction: "desc", label: "Status Z–A" },
]

const MONTHS = ["", "Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"]

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type UploadStatus = "pending" | "processing" | "completed" | "failed" | "cancelled"

type UploadRow = {
  id: number
  active_filename: string
  inactive_filename: string
  period_year: number
  period_month: number
  period_label: string
  status: UploadStatus
  active_row_count: number
  inactive_row_count: number
  total_row_count: number
  replaced_row_count: number
  error_message: string | null
  imported_at: string | null
  created_at: string
  uploaded_by: string | null
}

interface PreviewData {
  period_year: number
  period_month: number
  period_label: string
  active_row_count: number
  inactive_row_count: number
  total_row_count: number
  existing_active_row_count: number
  existing_inactive_row_count: number
  existing_total_row_count: number
  will_replace: boolean
  is_unchanged: boolean
}

type StatusUpdate = {
  type: "status_update"
  upload_id: number
  status: UploadStatus
  active_row_count?: number | null
  inactive_row_count?: number | null
  total_row_count?: number | null
  error_message?: string | null
}

type ProgressUpdate = {
  type: "progress_update"
  upload_id: number
  progress_rows: number
}

type Filters = {
  year: string | null
  month: string | null
  status: string | null
  search: string | null
}

// ---------------------------------------------------------------------------
// Page Component
// ---------------------------------------------------------------------------

export default function MasterOutletUploads({
  uploads,
  total,
  page,
  per_page,
  sort,
  direction,
  filters,
  available_years,
}: {
  uploads: UploadRow[]
  total: number
  page: number
  per_page: number
  sort: string
  direction: "asc" | "desc"
  filters: Filters
  available_years: number[]
}) {
  const currentYear = new Date().getFullYear()

  // Form State
  const [periodYear, setPeriodYear] = React.useState(currentYear)
  const [periodMonth, setPeriodMonth] = React.useState(new Date().getMonth() + 1)
  const [activeFile, setActiveFile] = React.useState<File | null>(null)
  const [inactiveFile, setInactiveFile] = React.useState<File | null>(null)

  const [activeDrag, setActiveDrag] = React.useState(false)
  const [inactiveDrag, setInactiveDrag] = React.useState(false)

  // Loading & Modal State
  const [previewLoading, setPreviewLoading] = React.useState(false)
  const [previewError, setPreviewError] = React.useState<string | null>(null)
  const [previewData, setPreviewData] = React.useState<PreviewData | null>(null)
  const [previewModalOpen, setPreviewModalOpen] = React.useState(false)
  const [importing, setImporting] = React.useState(false)

  const [searchValue, setSearchValue] = React.useState(filters.search ?? "")

  // Sync search input when URL changes (browser back/forward)
  React.useEffect(() => {
    setSearchValue(filters.search ?? "")
  }, [filters.search])

  // Live uploads tracking in-flight changes
  const [liveUploads, setLiveUploads] = React.useState<(UploadRow & { progress_rows?: number })[]>(uploads)
  React.useEffect(() => { setLiveUploads(uploads) }, [uploads])

  const liveInFlightKey = React.useMemo(
    () =>
      liveUploads
        .filter((u) => u.status === "pending" || u.status === "processing")
        .map((u) => u.id)
        .sort((a, b) => a - b)
        .join(","),
    [liveUploads]
  )

  // ActionCable connection for real-time progress update
  React.useEffect(() => {
    if (!liveInFlightKey) return
    const ids = liveInFlightKey.split(",").map((s) => parseInt(s, 10))
    const subs = ids.map((id) =>
      consumer.subscriptions.create(
        { channel: "MasterOutletUploadChannel", upload_id: id },
        {
          received(data: StatusUpdate | ProgressUpdate) {
            if (data.type === "progress_update") {
              setLiveUploads((prev) =>
                prev.map((u) =>
                  u.id === data.upload_id
                    ? { ...u, progress_rows: data.progress_rows }
                    : u
                )
              )
            } else if (data.type === "status_update") {
              setLiveUploads((prev) =>
                prev.map((u) =>
                  u.id === data.upload_id
                    ? {
                        ...u,
                        status: data.status,
                        active_row_count: data.active_row_count ?? u.active_row_count,
                        inactive_row_count: data.inactive_row_count ?? u.inactive_row_count,
                        total_row_count: data.total_row_count ?? u.total_row_count,
                        error_message: data.error_message ?? u.error_message,
                      }
                    : u
                )
              )
              // Reload page once an upload finishes to get updated tables from DB
              if (data.status === "completed" || data.status === "failed" || data.status === "cancelled") {
                router.reload({ only: ["uploads"] })
              }
            }
          },
        }
      )
    )
    return () => subs.forEach((s) => s.unsubscribe())
  }, [liveInFlightKey])

  // Navigation / URL filters query state
  function navigate(overrides: Record<string, string | number | null>) {
    const params: Record<string, string | number> = {}
    if (filters.year)          params.year      = filters.year
    if (filters.month)         params.month     = filters.month
    if (filters.status)        params.status    = filters.status
    if (filters.search)        params.search    = filters.search
    if (sort !== "created_at") params.sort      = sort
    if (direction !== "desc")  params.direction = direction
    if (page > 1)              params.page      = page
    
    Object.entries(overrides).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== "") params[k] = v as string | number
      else delete params[k]
    })
    
    router.get("/admin/master-outlet/uploads", params as Record<string, string>, {
      preserveScroll: true,
    })
  }

  function handleSortColumn(col: string) {
    if (sort === col) {
      navigate({ direction: direction === "asc" ? "desc" : "asc", page: null })
    } else {
      navigate({ sort: col, direction: "asc", page: null })
    }
  }

  // Load preview data before upload
  async function handleLoadPreview() {
    if (!activeFile || !inactiveFile) return
    setPreviewLoading(true)
    setPreviewError(null)

    const formData = new FormData()
    formData.append("active_file", activeFile)
    formData.append("inactive_file", inactiveFile)
    formData.append("period_year", String(periodYear))
    formData.append("period_month", String(periodMonth))

    try {
      const resp = await fetch("/admin/master-outlet/uploads/preview", {
        method: "POST",
        headers: {
          "X-CSRF-Token": getCsrfToken(),
        },
        body: formData,
      })

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}))
        throw new Error(err.error ?? `HTTP error! status: ${resp.status}`)
      }

      const data: PreviewData = await resp.json()
      setPreviewData(data)
      setPreviewModalOpen(true)
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : "Kesalahan tidak diketahui")
    } finally {
      setPreviewLoading(false)
    }
  }

  // Submit files to DB and start processing job
  function handleConfirmUpload() {
    if (!activeFile || !inactiveFile || !previewData) return
    setPreviewModalOpen(false)

    router.post(
      "/admin/master-outlet/uploads",
      {
        active_file: activeFile,
        inactive_file: inactiveFile,
        period_year: previewData.period_year,
        period_month: previewData.period_month,
        active_row_count: previewData.active_row_count,
        inactive_row_count: previewData.inactive_row_count,
      },
      {
        forceFormData: true,
        onStart: () => setImporting(true),
        onFinish: () => {
          setImporting(false)
          setActiveFile(null)
          setInactiveFile(null)
          setPreviewData(null)
        },
      }
    )
  }

  // Cancel processing upload
  async function handleCancelUpload(id: number) {
    try {
      await fetch(`/admin/master-outlet/uploads/${id}/cancel`, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": getCsrfToken(),
        },
      })
    } catch (err) {
      console.error("Gagal membatalkan upload:", err)
    }
  }

  // Computations
  const inFlightUploads = liveUploads.filter((u) => u.status === "pending" || u.status === "processing")
  const visibleUploads = liveUploads.filter((u) => u.status !== "pending" && u.status !== "processing")

  const hasActiveFilter = !!filters.year || !!filters.month || !!filters.status || !!filters.search
  const yearsList = React.useMemo(() => {
    const list = [...available_years]
    if (!list.includes(currentYear)) list.push(currentYear)
    if (!list.includes(currentYear - 1)) list.push(currentYear - 1)
    if (!list.includes(currentYear - 2)) list.push(currentYear - 2)
    return list.sort((a, b) => b - a)
  }, [available_years, currentYear])

  const totalPages = Math.ceil(total / per_page)

  const { filterOpen, setFilterOpen, sortOpen, setSortOpen, activeFilterCount, applyFilters, resetFilters } =
    useMobileFilterSort(filters, navigate, ["year", "month", "status", "search"] as const)
  
  const sortLabel = SORT_OPTIONS.find((o) => o.sort === sort && o.direction === direction)?.label ?? "Urutkan"

  return (
    <>
      <Head title="Master Outlet Uploads">
        <meta name="description" content="Upload dan kelola data master outlet nasional (active & inactive) bulanan." />
        <meta property="og:title" content="Master Outlet Uploads" />
        <meta property="og:description" content="Upload dan kelola data master outlet nasional (active & inactive) bulanan." />
      </Head>

      <AppShell>
        {/* Header */}
        <div className="border-b border-hairline pb-6 mb-6">
          <h1>Master Outlet</h1>
          <p className="mt-1">Upload baseline data outlet nasional bulanan (Active &amp; Inactive).</p>
        </div>

        <MonthlyUploadAlert categoryKey="master_outlet" label="Master Outlet" />

        {/* In-Flight progress list */}
        {inFlightUploads.length > 0 && (
          <div className="mb-8 space-y-3">
            <h2 className="text-sm font-semibold text-ink-muted uppercase tracking-wider">
              Upload Sedang Diproses ({inFlightUploads.length})
            </h2>
            <div className="grid grid-cols-1 gap-3">
              {inFlightUploads.map((u) => (
                <ProgressCard key={u.id} upload={u} onCancel={() => handleCancelUpload(u.id)} />
              ))}
            </div>
          </div>
        )}

        {/* Upload form card */}
        <div className="bg-surface rounded-xl border border-hairline p-6 shadow-sm mb-8">
          <h2 className="text-lg font-semibold text-ink-display mb-4">Upload Master Outlet Baru</h2>
          
          <div className="space-y-6">
            {/* Period selectors */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md">
              <div>
                <label htmlFor="period-year" className="block text-xs font-semibold text-ink-muted uppercase tracking-wider mb-2">
                  Tahun
                </label>
                <Select
                  id="period-year"
                  value={periodYear}
                  onChange={(e) => setPeriodYear(Number(e.target.value))}
                >
                  {yearsList.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </Select>
              </div>
              
              <div>
                <label htmlFor="period-month" className="block text-xs font-semibold text-ink-muted uppercase tracking-wider mb-2">
                  Bulan
                </label>
                <Select
                  id="period-month"
                  value={periodMonth}
                  onChange={(e) => setPeriodMonth(Number(e.target.value))}
                >
                  {MONTHS.slice(1).map((m, idx) => (
                    <option key={idx + 1} value={idx + 1}>{m}</option>
                  ))}
                </Select>
              </div>
            </div>

            {/* Dual Drag & Drop Zone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Active File Dropzone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setActiveDrag(true); }}
                onDragLeave={() => setActiveDrag(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setActiveDrag(false);
                  const files = e.dataTransfer.files;
                  if (files && files.length > 0) {
                    const file = files[0];
                    if (file.name.endsWith(".xlsx")) setActiveFile(file);
                    else alert("Hanya file Excel (.xlsx) yang diperbolehkan.");
                  }
                }}
                className={`flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-lg text-center transition-colors min-h-[160px] ${
                  activeFile
                    ? "border-green-500 bg-green-50/10 dark:bg-green-950/10"
                    : activeDrag
                      ? "border-accent bg-surface/50"
                      : "border-hairline hover:bg-page/50"
                }`}
              >
                {activeFile ? (
                  <div className="flex flex-col items-center">
                    <CheckCircle2 className="h-10 w-10 text-green-500 mb-2" />
                    <span className="text-sm font-medium text-ink-display truncate max-w-[240px] block" title={activeFile.name}>
                      {activeFile.name}
                    </span>
                    <span className="text-xs text-ink-muted mt-0.5">
                      {(activeFile.size / 1024 / 1024).toFixed(2)} MB
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-3 text-red-500 hover:text-red-600 hover:bg-red-50/50"
                      onClick={() => setActiveFile(null)}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Hapus
                    </Button>
                  </div>
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-ink-muted mb-2" />
                    <span className="text-sm font-medium text-ink-display">
                      File Active (ALL_OUTLET_ACTIVE.xlsx)
                    </span>
                    <span className="text-xs text-ink-muted mt-1">
                      Drag &amp; drop atau klik untuk memilih file
                    </span>
                    <input
                      type="file"
                      accept=".xlsx"
                      className="hidden"
                      id="active-file-input"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setActiveFile(file);
                      }}
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      className="mt-3"
                      onClick={() => document.getElementById("active-file-input")?.click()}
                    >
                      Pilih File
                    </Button>
                  </>
                )}
              </div>

              {/* Inactive File Dropzone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setInactiveDrag(true); }}
                onDragLeave={() => setInactiveDrag(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setInactiveDrag(false);
                  const files = e.dataTransfer.files;
                  if (files && files.length > 0) {
                    const file = files[0];
                    if (file.name.endsWith(".xlsx")) setInactiveFile(file);
                    else alert("Hanya file Excel (.xlsx) yang diperbolehkan.");
                  }
                }}
                className={`flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-lg text-center transition-colors min-h-[160px] ${
                  inactiveFile
                    ? "border-green-500 bg-green-50/10 dark:bg-green-950/10"
                    : inactiveDrag
                      ? "border-accent bg-surface/50"
                      : "border-hairline hover:bg-page/50"
                }`}
              >
                {inactiveFile ? (
                  <div className="flex flex-col items-center">
                    <CheckCircle2 className="h-10 w-10 text-green-500 mb-2" />
                    <span className="text-sm font-medium text-ink-display truncate max-w-[240px] block" title={inactiveFile.name}>
                      {inactiveFile.name}
                    </span>
                    <span className="text-xs text-ink-muted mt-0.5">
                      {(inactiveFile.size / 1024 / 1024).toFixed(2)} MB
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-3 text-red-500 hover:text-red-600 hover:bg-red-50/50"
                      onClick={() => setInactiveFile(null)}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Hapus
                    </Button>
                  </div>
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-ink-muted mb-2" />
                    <span className="text-sm font-medium text-ink-display">
                      File Inactive (ALL_OUTLET_INACTIVE.xlsx)
                    </span>
                    <span className="text-xs text-ink-muted mt-1">
                      Drag &amp; drop atau klik untuk memilih file
                    </span>
                    <input
                      type="file"
                      accept=".xlsx"
                      className="hidden"
                      id="inactive-file-input"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setInactiveFile(file);
                      }}
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      className="mt-3"
                      onClick={() => document.getElementById("inactive-file-input")?.click()}
                    >
                      Pilih File
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-3">
              <Button
                variant="primary"
                onClick={handleLoadPreview}
                disabled={!activeFile || !inactiveFile || previewLoading || importing}
              >
                {importing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Mengunggah &amp; Memproses...
                  </>
                ) : previewLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Memproses Preview...
                  </>
                ) : (
                  "Preview & Upload"
                )}
              </Button>
              <Button
                variant="secondary"
                disabled={previewLoading || (!activeFile && !inactiveFile) || importing}
                onClick={() => {
                  setActiveFile(null);
                  setInactiveFile(null);
                }}
              >
                Clear
              </Button>
            </div>

            {importing && (
              <div className="rounded-md border border-accent bg-accent/5 p-4 text-sm text-ink-body">
                <div className="flex items-center gap-2 font-medium">
                  <Loader2 className="h-4 w-4 animate-spin text-accent" />
                  Mengunggah file ke server...
                </div>
                <p className="mt-1 text-xs text-ink-muted">
                  Proses ini dapat memakan waktu beberapa saat tergantung pada ukuran file dan kecepatan koneksi Anda. Mohon tunggu...
                </p>
              </div>
            )}
            
            {previewError && (
              <div className="rounded-md border border-red-200 bg-red-50/50 dark:border-red-900/50 p-4 text-sm text-red-700 dark:text-red-300">
                <div className="flex items-center gap-2 font-medium">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  Gagal membuat preview
                </div>
                <p className="mt-1 text-xs">{previewError}</p>
              </div>
            )}
          </div>
        </div>

        {/* History Table / Lists */}
        <div>
          <h2 className="text-lg font-semibold text-ink-display mb-4">Riwayat Upload</h2>

          {/* Mobile Filter / Sort bar */}
          <div className="mb-4 md:hidden">
            <MobileFilterSortBar
              filterCount={activeFilterCount}
              sortLabel={sortLabel}
              onFilterClick={() => setFilterOpen(true)}
              onSortClick={() => setSortOpen(true)}
            />
          </div>

          {/* Desktop Filter / Sort bar */}
          <div className="mb-4 hidden flex-wrap items-end gap-3 md:flex">
            <Select
              value={filters.year ?? ""}
              onChange={(e) => navigate({ year: e.target.value || null, page: null })}
              className="w-28"
            >
              <option value="">Semua Tahun</option>
              {yearsList.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </Select>

            <Select
              value={filters.month ?? ""}
              onChange={(e) => navigate({ month: e.target.value || null, page: null })}
              className="w-36"
            >
              <option value="">Semua Bulan</option>
              {MONTHS.slice(1).map((name, i) => (
                <option key={i + 1} value={i + 1}>{name}</option>
              ))}
            </Select>

            <Select
              value={filters.status ?? ""}
              onChange={(e) => navigate({ status: e.target.value || null, page: null })}
              className="w-36"
            >
              <option value="">Semua Status</option>
              {(["pending", "processing", "completed", "failed", "cancelled"] as const).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>

            <form
              onSubmit={(e) => {
                e.preventDefault()
                navigate({ search: searchValue || null, page: null })
              }}
            >
              <Input
                type="search"
                placeholder="Cari filename…"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onBlur={() => navigate({ search: searchValue || null, page: null })}
                className="w-52"
              />
            </form>

            {hasActiveFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  navigate({ year: null, month: null, status: null, search: null, page: null })
                }
              >
                Reset filter
              </Button>
            )}
          </div>

          {/* Results list */}
          {visibleUploads.length === 0 ? (
            <p className="text-sm text-ink-muted mt-6">
              {hasActiveFilter
                ? "Tidak ada upload yang cocok dengan filter."
                : "Belum ada riwayat upload."}
            </p>
          ) : (
            <>
              {/* Mobile Card list */}
              <div className="space-y-3 md:hidden">
                {visibleUploads.map((u) => (
                  <UploadCard key={u.id} upload={u} />
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden overflow-hidden rounded-xl border border-hairline bg-surface md:block">
                <table className="w-full text-sm">
                  <thead className="bg-surface border-b border-hairline">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-ink-muted">Files</th>
                      <SortableHeader
                        col="period" label="Periode"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                      <SortableHeader
                        col="active_row_count" label="Active Rows" align="right"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                      <SortableHeader
                        col="inactive_row_count" label="Inactive Rows" align="right"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                      <SortableHeader
                        col="total_row_count" label="Total Rows" align="right"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                      <SortableHeader
                        col="status" label="Status"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                      <th className="px-4 py-3 text-left font-semibold text-ink-muted">Pengunggah</th>
                      <SortableHeader
                        col="created_at" label="Waktu"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-hairline">
                    {visibleUploads.map((u) => (
                      <UploadTableRow key={u.id} upload={u} />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Pagination */}
          {total > 0 && (
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-ink-muted">
                Menampilkan {(page - 1) * per_page + 1}–{Math.min(page * per_page, total)} dari{" "}
                {total} upload
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => navigate({ page: page - 1 })}
                  >
                    Sebelumnya
                  </Button>
                  <span className="text-sm text-ink-muted">
                    Hal. {page} / {totalPages}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => navigate({ page: page + 1 })}
                  >
                    Berikutnya
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mobile filter sheet */}
        <MobileFilterSheet
          open={filterOpen}
          onOpenChange={setFilterOpen}
          initial={{
            year: filters.year ?? "",
            month: filters.month ?? "",
            status: filters.status ?? "",
            search: filters.search ?? "",
          }}
          onApply={applyFilters}
          onReset={resetFilters}
        >
          {(draft, setDraft) => (
            <>
              <label className="block">
                <span className="mb-1 block text-sm font-semibold text-ink-display">Tahun</span>
                <Select
                  value={draft.year}
                  onChange={(e) => setDraft({ ...draft, year: e.target.value })}
                >
                  <option value="">Semua Tahun</option>
                  {yearsList.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </Select>
              </label>
              <label className="block mt-4">
                <span className="mb-1 block text-sm font-semibold text-ink-display">Bulan</span>
                <Select
                  value={draft.month}
                  onChange={(e) => setDraft({ ...draft, month: e.target.value })}
                >
                  <option value="">Semua Bulan</option>
                  {MONTHS.slice(1).map((name, i) => (
                    <option key={i + 1} value={String(i + 1)}>{name}</option>
                  ))}
                </Select>
              </label>
              <label className="block mt-4">
                <span className="mb-1 block text-sm font-semibold text-ink-display">Status</span>
                <Select
                  value={draft.status}
                  onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                >
                  <option value="">Semua Status</option>
                  {(["pending", "processing", "completed", "failed", "cancelled"] as const).map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </Select>
              </label>
              <label className="block mt-4">
                <span className="mb-1 block text-sm font-semibold text-ink-display">Cari Filename</span>
                <Input
                  type="search"
                  value={draft.search}
                  onChange={(e) => setDraft({ ...draft, search: e.target.value })}
                />
              </label>
            </>
          )}
        </MobileFilterSheet>

        {/* Mobile sort sheet */}
        <MobileSortSheet
          open={sortOpen}
          onOpenChange={setSortOpen}
          current={{ sort, direction }}
          options={SORT_OPTIONS}
          onSelect={(opt) => {
            navigate({ sort: opt.sort, direction: opt.direction })
            setSortOpen(false)
          }}
        />

        {/* Preview Confirmation Dialog */}
        <Dialog open={previewModalOpen} onOpenChange={setPreviewModalOpen}>
          <DialogContent size="sm">
            <DialogHeader>
              <DialogTitle>Konfirmasi Import Master Outlet</DialogTitle>
            </DialogHeader>

            {previewData && (
              <div className="space-y-4">
                <p className="text-sm text-ink-body">
                  Anda akan mengimpor data Master Outlet untuk periode{" "}
                  <strong className="text-ink-display font-semibold">{previewData.period_label}</strong>.
                </p>

                {previewData.will_replace ? (
                  <div className="rounded-lg border border-yellow-200 bg-yellow-50/50 dark:border-yellow-900/50 p-3 text-xs text-yellow-800 dark:text-yellow-200">
                    <div className="flex items-center gap-1.5 font-medium mb-1">
                      <AlertCircle className="h-4 w-4 shrink-0 text-yellow-500" />
                      Peringatan: Overwrite Periode Terdeteksi
                    </div>
                    Data yang sudah diunggah sebelumnya untuk periode ini akan dihapus dan diganti sepenuhnya.
                  </div>
                ) : (
                  <div className="rounded-lg border border-green-200 bg-green-50/50 dark:border-green-900/50 p-3 text-xs text-green-800 dark:text-green-200">
                    Ini adalah upload baru untuk periode ini.
                  </div>
                )}

                <div className="overflow-hidden rounded-lg border border-hairline text-xs">
                  <div className="grid grid-cols-3 bg-surface border-b border-hairline px-3 py-2 font-semibold text-ink-muted">
                    <span>Data</span>
                    <span className="text-right">Sebelumnya</span>
                    <span className="text-right text-accent font-medium">Baru</span>
                  </div>
                  <div className="divide-y divide-hairline">
                    <div className="grid grid-cols-3 px-3 py-2">
                      <span className="text-ink-muted">Active Rows</span>
                      <span className="text-right tabular-nums">
                        {previewData.will_replace ? previewData.existing_active_row_count.toLocaleString() : "0"}
                      </span>
                      <span className="text-right tabular-nums text-accent">
                        {previewData.active_row_count.toLocaleString()}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 px-3 py-2">
                      <span className="text-ink-muted">Inactive Rows</span>
                      <span className="text-right tabular-nums">
                        {previewData.will_replace ? previewData.existing_inactive_row_count.toLocaleString() : "0"}
                      </span>
                      <span className="text-right tabular-nums text-accent">
                        {previewData.inactive_row_count.toLocaleString()}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 px-3 py-2 font-semibold bg-surface/50">
                      <span className="text-ink-display">Total Rows</span>
                      <span className="text-right tabular-nums">
                        {previewData.will_replace ? previewData.existing_total_row_count.toLocaleString() : "0"}
                      </span>
                      <span className="text-right tabular-nums text-accent">
                        {previewData.total_row_count.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <DialogClose asChild>
                <Button variant="secondary">Batal</Button>
              </DialogClose>
              <Button variant="primary" onClick={handleConfirmUpload}>
                Konfirmasi &amp; Import
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </AppShell>
    </>
  )
}

// ---------------------------------------------------------------------------
// Helper Components
// ---------------------------------------------------------------------------

function SortableHeader({
  col,
  label,
  sort,
  direction,
  onSort,
  align = "left",
}: {
  col: string
  label: string
  sort: string
  direction: "asc" | "desc"
  onSort: (col: string) => void
  align?: "left" | "right"
}) {
  const active = sort === col
  return (
    <th
      className={`cursor-pointer select-none px-4 py-3 font-semibold text-ink-muted hover:text-ink-body text-${align}`}
      onClick={() => onSort(col)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active ? (
          direction === "asc" ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-40" />
        )}
      </span>
    </th>
  )
}

function UploadTableRow({ upload }: { upload: UploadRow }) {
  return (
    <tr className="hover:bg-page/20 transition-colors">
      <td className="px-4 py-3 max-w-[280px]">
        <div className="text-xs text-ink-muted space-y-0.5">
          <p className="truncate" title={upload.active_filename}>
            Active: <span className="text-ink-body font-mono font-medium">{upload.active_filename}</span>
          </p>
          <p className="truncate" title={upload.inactive_filename}>
            Inactive: <span className="text-ink-body font-mono font-medium">{upload.inactive_filename}</span>
          </p>
        </div>
      </td>
      <td className="px-4 py-3 text-ink-body font-medium">{upload.period_label}</td>
      <td className="px-4 py-3 text-right tabular-nums text-ink-body">
        {upload.active_row_count > 0 ? upload.active_row_count.toLocaleString() : "—"}
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-ink-body">
        {upload.inactive_row_count > 0 ? upload.inactive_row_count.toLocaleString() : "—"}
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-ink-body font-semibold">
        {upload.total_row_count > 0 ? upload.total_row_count.toLocaleString() : "—"}
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={upload.status} />
        {upload.status === "failed" && upload.error_message && (
          <p className="text-[10px] text-red-500 mt-1 max-w-[150px] truncate" title={upload.error_message}>
            {upload.error_message}
          </p>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-ink-muted truncate max-w-[120px]" title={upload.uploaded_by || ""}>
        {upload.uploaded_by}
      </td>
      <td className="px-4 py-3 text-xs text-ink-muted">
        {upload.imported_at ? formatDate(upload.imported_at) : formatDate(upload.created_at)}
      </td>
    </tr>
  )
}

function UploadCard({ upload }: { upload: UploadRow }) {
  return (
    <DataCard>
      <DataCardHeader>
        <DataCardTitle>Periode: {upload.period_label}</DataCardTitle>
        <DataCardStatus><StatusBadge status={upload.status} /></DataCardStatus>
      </DataCardHeader>
      <div className="px-4 py-2 border-b border-hairline bg-page/10 space-y-0.5 text-xs text-ink-muted">
        <p className="truncate" title={upload.active_filename}>Active: <span className="font-mono text-ink-body">{upload.active_filename}</span></p>
        <p className="truncate" title={upload.inactive_filename}>Inactive: <span className="font-mono text-ink-body">{upload.inactive_filename}</span></p>
      </div>
      <DataCardGrid>
        <DataCardField label="Active Rows" value={upload.active_row_count > 0 ? upload.active_row_count.toLocaleString() : "—"} />
        <DataCardField label="Inactive Rows" value={upload.inactive_row_count > 0 ? upload.inactive_row_count.toLocaleString() : "—"} />
        <DataCardField wide label="Total Rows" value={upload.total_row_count > 0 ? upload.total_row_count.toLocaleString() : "—"} />
        <DataCardField label="Pengunggah" value={upload.uploaded_by ?? "—"} />
        <DataCardField
          label="Waktu"
          value={upload.imported_at ? formatDate(upload.imported_at) : formatDate(upload.created_at)}
        />
        {upload.status === "failed" && upload.error_message && (
          <div className="col-span-2 text-xs text-red-500 mt-1 break-words">
            Gagal: {upload.error_message}
          </div>
        )}
      </DataCardGrid>
    </DataCard>
  )
}

function ProgressCard({ upload, onCancel }: { upload: UploadRow & { progress_rows?: number }; onCancel: () => void }) {
  const isInFlight = upload.status === "pending" || upload.status === "processing"
  const totalRows = upload.total_row_count ?? 0
  const progressRows = upload.progress_rows ?? 0
  const showProgress = upload.status === "processing" && progressRows > 0

  const progressPercent = totalRows > 0 && progressRows > 0
    ? Math.min(Math.round((progressRows / totalRows) * 100), 99) // Limit to 99% until complete
    : undefined

  return (
    <div className="rounded-xl border border-hairline bg-surface p-4 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-ink-display truncate" title={upload.period_label}>
            Periode: {upload.period_label}
          </p>
          <div className="mt-1 space-y-0.5 text-xs text-ink-muted">
            <p className="truncate" title={upload.active_filename}>Active: <span className="text-ink-body font-mono">{upload.active_filename}</span></p>
            <p className="truncate" title={upload.inactive_filename}>Inactive: <span className="text-ink-body font-mono">{upload.inactive_filename}</span></p>
          </div>
          {upload.status === "completed" && upload.total_row_count != null && (
            <p className="mt-1.5 text-xs text-ink-muted">
              Selesai! {upload.total_row_count.toLocaleString()} baris diimport
            </p>
          )}
          {upload.status === "pending" && (
            <p className="mt-1.5 text-xs text-ink-muted">
              Menunggu antrian... ({totalRows > 0 ? `${totalRows.toLocaleString()} total baris` : "menghitung..."})
            </p>
          )}
          {showProgress && (
            <p className="mt-1.5 text-xs text-ink-muted">
              Sedang memproses...{" "}
              <strong className="text-ink-body tabular-nums">
                {progressRows.toLocaleString()}
              </strong>{" "}
              dari <strong className="text-ink-body tabular-nums">{totalRows.toLocaleString()}</strong> baris selesai
              {progressPercent != null && ` (${progressPercent}%)`}
            </p>
          )}
          {upload.error_message && (
            <p className="mt-1.5 text-xs text-red-600 dark:text-red-400 break-words" title={upload.error_message}>
              Gagal: {upload.error_message}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <StatusBadge status={upload.status} />
          {isInFlight && (
            <Button variant="secondary" size="sm" onClick={onCancel}>
              <Ban className="mr-1 h-3.5 w-3.5" />
              Batalkan
            </Button>
          )}
        </div>
      </div>
      {isInFlight && (
        <div className="mt-3">
          <ProgressBar
            value={progressPercent}
            indeterminate={progressPercent === undefined}
          />
        </div>
      )}
    </div>
  )
}

function StatusBadge({ status }: { status: UploadStatus }) {
  switch (status) {
    case "pending":
      return (
        <Badge tone="muted">
          <Clock className="mr-1 h-3 w-3" />
          Pending
        </Badge>
      )
    case "processing":
      return (
        <Badge tone="accent">
          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          Processing
        </Badge>
      )
    case "completed":
      return (
        <Badge tone="success">
          <CheckCircle2 className="mr-1 h-3 w-3" />
          Completed
        </Badge>
      )
    case "failed":
      return (
        <Badge tone="danger">
          <XCircle className="mr-1 h-3 w-3" />
          Failed
        </Badge>
      )
    case "cancelled":
      return (
        <Badge tone="muted">
          <Ban className="mr-1 h-3 w-3" />
          Dibatalkan
        </Badge>
      )
  }
}

function ProgressBar({ value, indeterminate }: { value?: number; indeterminate?: boolean }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-hairline">
      {indeterminate ? (
        <div className="h-full w-1/3 animate-[progress-slide_1.4s_ease-in-out_infinite] rounded-full bg-accent" />
      ) : (
        <div
          className="h-full rounded-full bg-accent transition-all duration-300"
          style={{ width: `${Math.min(value ?? 0, 100)}%` }}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Utility functions
// ---------------------------------------------------------------------------

function getCsrfToken(): string {
  return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? ""
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("id-ID", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}
